import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Dropdown, DropDownButton } from '@/shared/components/Dropdown';
import { listServiceTypesApiService } from '@/epics/serviceManagement/api/listServiceTypesApiService';
import { viewServiceType } from '@/epics/serviceManagement/api/viewServiceTypeApiService';
import { MenuItemProps } from '@/shared/components/Menu/types';
import type { ServiceTypeListItem } from '@/epics/serviceManagement/types/serviceTypeTypes';
import { useSearchLoading } from '@/shared/hooks/useSearchLoading';
import { SUBSCRIPTION_MANAGEMENT_LABELS } from '@/epics/subscriptionManagment/constants/translations/labels';

interface ServiceTypeInfiniteDropdownProps {
  value: string;
  onChange: (value: string, serviceName: string) => void;
  /** Service type ids already added to the plan — shown disabled with a tooltip. */
  excludedIds?: string[];
  disabled?: boolean;
  hideChevron?: boolean;
  error?: boolean;
  errorMessage?: string;
  /**
   * Display name of the already-selected service type.
   * Shown immediately on render while the API fetches the full list,
   * so the dropdown trigger never blanks out during loading.
   */
  displayName?: string;
}

export const ServiceTypeInfiniteDropdown: React.FC<
  ServiceTypeInfiniteDropdownProps
> = ({
  value,
  onChange,
  excludedIds = [],
  disabled,
  hideChevron,
  error,
  errorMessage,
  displayName,
}) => {
  const [serviceTypes, setServiceTypes] = useState<ServiceTypeListItem[]>([]);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [resolvedServiceType, setResolvedServiceType] = useState<{
    service_name: string;
  } | null>(null);
  const [resolving, setResolving] = useState(false);
  // Separate from `loading` — pagination must never share it with search.
  const [loadingMore, setLoadingMore] = useState(false);
  const {
    search: searchQuery,
    setSearch: setSearchQuery,
    loading,
    setLoading,
  } = useSearchLoading('');

  // Own debounce tick, so a settle back to an unchanged value (e.g. type one
  // letter, delete it) still fires and resets `loading` — fixes it getting
  // stuck true when useSearchLoading's internal debounce bails out as a no-op.
  const [searchVersion, setSearchVersion] = useState(0);
  useEffect(() => {
    const timer = setTimeout(() => setSearchVersion((v) => v + 1), 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const mountedRef = useRef(true);
  useEffect(() => {
    // Reset on mount too — StrictMode double-invokes (mount/cleanup/mount).
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Mirrors `serviceTypes` without being a dependency of the resolving effect
  // below — lets that effect check "is this value already loaded?" using the
  // latest list on every value change, without re-running (and re-fetching)
  // every time a new page of serviceTypes arrives.
  const serviceTypesRef = useRef<ServiceTypeListItem[]>([]);
  useEffect(() => {
    serviceTypesRef.current = serviceTypes;
  }, [serviceTypes]);

  // Fetch selected service type by ID when it's not yet in the paginated list
  // (e.g. edit mode, service is page 10+). Skipped when the value is already
  // in the loaded list — e.g. right after the user picks an item from the
  // open menu — so selecting a visible item never flashes the shimmer.
  useEffect(() => {
    if (!value) {
      setResolvedServiceType(null);
      setResolving(false);
      return;
    }
    if (serviceTypesRef.current.some((s) => s.id === value)) {
      setResolvedServiceType(null);
      setResolving(false);
      return;
    }
    setResolving(true);
    viewServiceType(value).then((result) => {
      if (mountedRef.current && result.success && result.data) {
        setResolvedServiceType({
          service_name: result.data.service_name || value,
        });
      }
      if (mountedRef.current) {
        setResolving(false);
      }
    });
  }, [value]);

  // Load first page — also re-runs (replacing the list) on every debounce
  // tick, since search is server-side.
  useEffect(() => {
    let mounted = true;
    const fetchFirstPage = async () => {
      setLoading(true);
      try {
        const response = await listServiceTypesApiService.listServiceTypes({
          page: 1,
          limit: 10,
          status: 'active',
          ...(searchQuery ? { search: searchQuery } : {}),
        });
        if (mounted) {
          setServiceTypes(response.data);
          setHasMore(response.hasNextPage);
          setPage(1);
        }
      } catch (error) {
        console.error('Error fetching service types', error);
      } finally {
        if (mounted) {
          setLoading(false);
        }
      }
    };

    fetchFirstPage();

    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVersion, setLoading]);

  const fetchMore = useCallback(async () => {
    if (loadingMore || loading || !hasMore) {
      return;
    }
    setLoadingMore(true);
    const nextPage = page + 1;
    try {
      const response = await listServiceTypesApiService.listServiceTypes({
        page: nextPage,
        limit: 10,
        status: 'active',
        ...(searchQuery ? { search: searchQuery } : {}),
      });
      setServiceTypes((prev) => {
        const newItems = response.data.filter(
          (newItem) => !prev.some((item) => item.id === newItem.id)
        );
        return [...prev, ...newItems];
      });
      setHasMore(response.hasNextPage);
      setPage(response.page);
    } catch (error) {
      console.error('Error fetching more service types', error);
    } finally {
      setLoadingMore(false);
    }
  }, [loadingMore, loading, hasMore, page, searchQuery]);

  const serviceTypeItems: MenuItemProps[] = serviceTypes.map((serviceType) => {
    const isExcluded = excludedIds.includes(serviceType.id);
    return {
      id: serviceType.id,
      label: serviceType.service_name,
      disabled: isExcluded,
      ...(isExcluded && {
        disabledTooltip: 'This service has already been added to the plan',
        disabledTooltipSide: 'right' as const,
      }),
    };
  });

  // While a search-driven fetch is in flight, the list is replaced outright
  // by the spinner (not appended to stale results) — this covers typing AND
  // clearing the search, since both go through `loading`. Appending is
  // reserved for plain scroll-triggered pagination (`loadingMore`).
  const items: MenuItemProps[] = loading
    ? [{ id: '__loading__', label: 'Loading...', disabled: true }]
    : loadingMore
      ? [
          ...serviceTypeItems,
          { id: '__loading__', label: 'Loading...', disabled: true },
        ]
      : searchQuery && serviceTypes.length === 0
        ? [
            {
              id: '__no_results__',
              label: 'No results found',
              disabled: true,
            },
          ]
        : serviceTypeItems;

  const selectedServiceType = serviceTypes.find((s) => s.id === value);
  // Use the pre-provided displayName immediately (while API is fetching) so the
  // dropdown trigger never blanks out. Once the list arrives or the API completes,
  // prefer the actual data.
  const displayValue = selectedServiceType
    ? selectedServiceType.service_name
    : resolvedServiceType
      ? resolvedServiceType.service_name
      : displayName || (value ? `ID: ${value}` : '');

  return (
    <Dropdown
      menuFullWidth
      menuMaxVisibleItems={6}
      open={isDropdownOpen}
      onOpenChange={(open) => {
        setIsDropdownOpen(open);
        if (!open) {
          setSearchQuery('');
        }
      }}
      items={items}
      selectedId={value}
      showSearch
      searchPlaceholder="Search service..."
      onSearchChange={setSearchQuery}
      error={error ?? false}
      errorMessage={errorMessage ?? ''}
      onItemClick={(id) => {
        if (id !== '__loading__' && id !== '__no_results__') {
          const serviceType = serviceTypes.find((s) => s.id === id);
          onChange(id, serviceType?.service_name || '');
          setIsDropdownOpen(false);
        }
      }}
      toolTip={false}
      trigger={({ open, toggle, disabled: dropDisabled, error: hasError }) =>
        resolving ? (
          <div
            data-testid="service-type-dropdown"
            className="animate-pulse w-full min-h-[56px] rounded-lg bg-[#E5E7E9]"
          />
        ) : (
          <DropDownButton
            data-testid="service-type-dropdown"
            label={SUBSCRIPTION_MANAGEMENT_LABELS.FIELD_SELECT_A_SERVICE.polish}
            value={displayValue}
            open={open}
            toggle={toggle}
            disabled={Boolean(disabled || dropDisabled)}
            error={hasError}
            toolTip={false}
            hideChevron={hideChevron ?? false}
          />
        )
      }
      disabled={disabled ?? false}
      onScrollEnd={() => {
        if (hasMore && !loading && !loadingMore) {
          fetchMore();
        }
      }}
    />
  );
};
